/**
 * COINS — the risk/reward pickup layer.
 *
 * Owner spec (2026-08-02): "When the cyclist approaches a chain of coins, the
 * coins are picked up and added to the score... Make the coin chains appear
 * BEFORE vehicle chains, not in empty lanes, to make it more difficult to
 * collect them or require kicks."
 *
 * ---------------------------------------------------------------------------
 * 1. WHAT A COIN IS (frame of reference — get this wrong and nothing else works)
 *
 * A coin is a ROAD-FIXED WORLD OBJECT. Vehicles in this sim are moved at their
 * CLOSING speed (`playerSpeed + vehicleSpeed`) because the player entity is
 * pinned at z = 0 and the world is what scrolls (see sim/types.ts). A coin has
 * no speed of its own, so it closes on the player at exactly `playerSpeed`:
 *
 *     coin.z += playerSpeed * dt
 *
 * — the same rate the road scrolls. Everything below follows from that: a coin
 * born at z metres ahead reaches the player after `-z / playerSpeed` seconds,
 * while the vehicle it belongs to (born much further out) reaches them after
 * `-vz / (playerSpeed + vehicleSpeed)`.
 *
 * ---------------------------------------------------------------------------
 * 2. PLACEMENT — the CO-ARRIVAL rule, solved in the time domain
 *
 * A chain is laid ONLY off a COMMITTED vehicle placement: `onVehicleSpawn` is
 * called from world.ts once per SpawnCommand that actually became a vehicle,
 * with the SPONSOR entity itself. With probability `chainChance` the chain goes
 * into that vehicle's lane, and the geometry is solved so that
 *
 *     the chain's LAST coin reaches the player plane at  T_c,
 *     the sponsor's FRONT reaches the player plane at    T_c + delta,
 *     delta = uniform(pairLeadMin, pairLeadMax)          seconds.
 *
 * That is the ONLY frame the pairing can be stated in, and getting it wrong is
 * the bug the owner reported on 2026-08-02 ("some chains are free-standing in
 * an empty lane"). The old rule placed the last coin a fixed 15-25 m of PLAYER
 * TRAVEL before the vehicle's arrival. Metres of player travel are not metres
 * of separation: the vehicle closes at `playerSpeed + vehicleSpeed` while the
 * coins close at `playerSpeed` alone, so a 15-25 m player-travel lead is
 * 0.87-1.45 s of time, and in that time the sponsor is still
 *
 *     delta * closingSpeed  =  32-54 m
 *
 * behind the last coin at the instant the player takes it. Half a football
 * pitch of empty asphalt between the bait and the threat — a pairing that was
 * real in the schedule and invisible on screen. Solving for delta directly
 * makes the separation 13-20 m at run-start speeds and 22-35 m at full ramp:
 * the sponsor is looming right behind the last coin, which is the read.
 *
 * The solve uses the DIRECTOR'S SPEEDS AT COMMIT TIME. Both speeds ramp over
 * the run, and a command is committed up to `leadTime` (7.3 s at run start)
 * before it arrives, so the achieved delta drifts a little from the drawn one.
 * The drift is second-order (both terms ramp together, and the ramp is ~0.14
 * m/s per second against closing speeds of 37-64 m/s); coins.test.ts asserts
 * the ACHIEVED delta lands in a tolerant band over whole 240 s runs rather than
 * trusting the algebra.
 *
 * WHICH END OF THE CHAIN IS "LAST". The player is pinned at z = 0 and coins
 * ahead of them are NEGATIVE, so the coin with the GREATEST z is the nearest
 * one and is met FIRST; the chain's last coin in TIME is the one furthest
 * ahead, at the most negative z. The anchor above is therefore the FAR end
 * (`endZ`), and the rest of the chain runs BACK toward the player at `spacing`
 * intervals — `endZ + i * spacing`.
 *
 * Getting that backwards is not cosmetic: laying the chain the other way (far
 * end = anchor + i * spacing, running AWAY from the player) puts the coins the
 * player meets LAST up to (count-1) * spacing = 22.5 m further out than the
 * anchor, i.e. PAST the vehicle — the coins would lead the player into a lane
 * that is already sealed, with no time to leave. coins.test.ts pins the sign.
 *
 * The chain is ALWAYS between the player and its own sponsor and can never be
 * overtaken by it: the sponsor is behind it and arrives strictly LATER
 * (`delta > 0`), and a body cannot both stay behind and arrive first.
 *
 * THE ANCHOR IS MANDATORY. The per-coin vehicle clamp below may drop coins from
 * a chain; if it would drop the LAST one, the whole chain is refused instead —
 * a chain whose anchor is missing no longer ends where the pairing says it
 * does, which is precisely the free-standing read. Likewise a chain trimmed
 * below `minChainCoins` is refused outright: 2-3 coins read as debris.
 *
 * HOW FAR AHEAD A CHAIN CAN BE BORN is fixed by physics, not by taste. The
 * earliest anything downstream can know about a vehicle is the frame it spawns,
 * `leadTime` seconds before its arrival (spawner.ts header 7), so the last coin
 * is born at `playerSpeed * (leadTime - delta)` metres ahead: ~118 m at
 * run-start speeds, ~140 m at full ramp. That is inside the draw distance at
 * the start of a run, so the RENDER scales each coin in over a far window
 * rather than letting it pop (render/coins.ts) — a coin at 118 m is barely a
 * pixel across, so the ramp is invisible and the pop is gone.
 *
 * THE START GRACE. Owner bug, same playtest: "don't have coins immediately at
 * the start (collect on start)". The spawner emits its whole opening pipeline
 * on frame ONE, including arrivals only a second or two out, so a chain hung in
 * front of one of those opened the run with a free pickup at the player's nose.
 * The gate is stated on the COLLECTIBLE instant — a chain is refused unless its
 * NEAREST coin cannot enter the magnet window before `startGrace` — so it is
 * exactly the property that gets asserted, and it does not blank the whole
 * opening pipeline the way a blanket "no chains before t = N" would.
 *
 * ---------------------------------------------------------------------------
 * 2b. THE SPONSOR'S LIFETIME — kicked chains become LOOT
 *
 * A chain outliving its sponsor is the other way to end up free-standing. There
 * are exactly two ways for a sponsor to leave:
 *
 *   KICKED. The player answered the seal with a kick, and the wreck leaves the
 *     lane. Any coins still on the road STAY — they are the reward for taking
 *     the kick, and deleting them would punish the correct play. That is the
 *     DEFAULT: nothing in this module deletes a coin on a kick, deliberately.
 *     In practice the window is empty anyway — a kick lands within a few tenths
 *     of a second of the sponsor reaching the player, by which time the chain
 *     has already gone by (measured: zero surviving coins over 24 minutes of
 *     coin-greedy riding across 6 seeds, magnet on and off).
 *
 *   DESPAWNED NORMALLY (drove past the player). This CANNOT happen while the
 *     chain is alive, and that is a consequence of the co-arrival rule rather
 *     than a rule of its own: every coin in the chain reaches the player
 *     strictly BEFORE the sponsor does, and a coin is released `despawnBehind`
 *     metres past the player — sooner than the vehicle's own, larger despawn
 *     threshold. So the coins are always gone first. coins.test.ts asserts it
 *     on every `despawn` event over 240 s runs; if it ever fires, the timing
 *     solve is wrong again.
 *
 * CLAMPS, and the level they act at:
 *   - PER CHAIN, before anything is placed: the chain's whole z span must be
 *     `minCoinGap` clear of every live coin in that lane. Two chains must never
 *     interleave into one unreadable dotted line — and because a chain plus its
 *     margins covers ~40 m of lane, this doubles as the natural THROTTLE on how
 *     often one lane can receive a chain, with no extra bookkeeping.
 *   - PER COIN: a coin that would land inside or right behind a live VEHICLE in
 *     its lane (`minVehicleGap` clear of the bodywork) is skipped; the rest of
 *     the chain still goes down.
 * The coin-gap clamp is deliberately NOT per coin: coins WITHIN a chain sit
 * `spacing` (2.5 m) apart, far inside `minCoinGap`, so a per-coin test against
 * the live pool would reject everything after the first coin and every "chain"
 * would be a single coin.
 *
 * ---------------------------------------------------------------------------
 * 3. DETERMINISM — and why the traffic stream cannot move
 *
 * The chain decision consumes randomness from a DEDICATED child stream,
 * `mulberry32(seed ^ COIN_SEED_SALT)`, owned by this module. It never touches
 * the spawner's rng, it runs strictly DOWNSTREAM of the spawner (on commands it
 * has already emitted), and it cannot feed anything back. The vehicle stream a
 * seed produces is therefore bit-identical with coins on and coins off — pinned
 * by coins.test.ts, which replays a long spawn sequence both ways, and by every
 * pre-existing spawner sweep staying green.
 *
 * Draws per spawn command, in this fixed order:
 *   1. the CHAIN CHANCE draw — always taken,
 *   2. the DELTA draw, 3. the COUNT draw — taken only when (1) succeeded, and
 *      taken BEFORE any geometry is examined, so a chain that is then dropped
 *      for the start grace, pool exhaustion or overlap cannot shift the stream
 *      either.
 *
 * ---------------------------------------------------------------------------
 * 4. COLLECTION
 *
 * A coin is collected on the STEP it is within `magnetRadius` metres ahead of
 * the player plane and `magnetHalfWidth` metres laterally. It is an instant,
 * deterministic state change: the coin leaves the pool, a `coin` SimEvent is
 * emitted and the score is credited that step. The Temple-Run "suck" is
 * PURELY RENDER (render/coinFlight.ts): the event hands the render a world
 * position, which it projects to screen and flies to the HUD counter. Nothing
 * about the animation can feed back into the sim, so a dropped frame cannot
 * cost a coin.
 *
 * LANE DISCIPLINE: `magnetHalfWidth` is 1.6 m against a 2.7 m lane, so a coin
 * one lane over (2.7 m) is never pulled and one two lanes over (5.4 m) is not
 * even close.
 *
 * ---------------------------------------------------------------------------
 * 5. ALLOCATION
 *
 * The pool is built once at construction and never grows. `update`, `collect`
 * and `onVehicleSpawn` mutate slots in place; the only object created per
 * pickup is the SimEvent, which is exactly what every other sim event costs.
 */

import { mulberry32, type Rng } from "../core/rng";
import { TUNING } from "./tuning";
import { isLive, laneCenterX, modelLength } from "./vehicles";
import type { CoinEntity, SimEvent, VehicleEntity } from "./types";
import type { Scoring } from "./scoring";

const C = TUNING.coins;

/**
 * Salt mixed into the world seed for the coin rng. Distinct from the kick
 * (0x5eed) and ambient (0xa7b) salts so the three streams never coincide.
 */
export const COIN_SEED_SALT = 0xc01d;

/** A coin slot: a CoinEntity plus its pool liveness flag. */
interface CoinSlot extends CoinEntity {
  _alive: boolean;
}

/**
 * The coin layer. One per world; `reset(seed)` rewinds it for a new run.
 */
export interface CoinField {
  /** Pooled coin slots. Dead slots are recycled — check `active`. */
  readonly coins: CoinEntity[];
  /** Chains dropped because the pool had no free slot (diagnostic). */
  readonly drops: number;
  /** Chains actually laid (diagnostic / tests). */
  readonly chains: number;

  /**
   * A vehicle has just been committed. Maybe lay a chain in ITS lane, ending
   * `pairLead*` seconds of arrival time in front of it (see header 2).
   *
   * @param sponsor       the vehicle that was just spawned. Its lane, z and
   *                      MODEL LENGTH (the pairing is on its FRONT) are read.
   * @param closingSpeed  playerSpeed + vehicleSpeed for that vehicle.
   * @param playerSpeed   current player forward speed (m/s).
   * @param runTime       sim time of this step — the start-grace clock.
   * @param vehicles      the live vehicle pool, for the overlap clamp.
   */
  onVehicleSpawn(
    sponsor: VehicleEntity,
    closingSpeed: number,
    playerSpeed: number,
    runTime: number,
    vehicles: readonly VehicleEntity[],
  ): void;

  /**
   * How many live coins still name `vehicleId` as their sponsor.
   *
   * Diagnostic only: the co-arrival rule makes this 0 for any vehicle that is
   * about to despawn normally, which is what coins.test.ts asserts.
   */
  sponsorCoins(vehicleId: number): number;

  /** Advance every coin toward the player and recycle the ones that passed. */
  update(dt: number, playerSpeed: number): void;

  /**
   * Collect every coin inside the magnet window. Emits one `coin` event per
   * pickup and credits the scoring controller.
   */
  collect(playerX: number, time: number, events: SimEvent[], scoring: Scoring): void;

  /** Rewind for a new run (re-seeds the rng, frees every slot). */
  reset(seed: number): void;
}

export function createCoinField(seed: number): CoinField {
  const slots: CoinSlot[] = [];
  for (let i = 0; i < C.poolSize; i++) {
    slots.push({
      id: i,
      x: 0,
      z: 0,
      active: false,
      phase: 0,
      sponsor: -1,
      chain: -1,
      _alive: false,
    });
  }
  /** Round-robin cursor, so recycled slots cycle rather than reusing slot 0. */
  let cursor = 0;
  let rng: Rng = mulberry32((seed ^ COIN_SEED_SALT) >>> 0);
  /** Monotonic per-run spawn serial — seeds each coin's visual spin phase. */
  let serial = 0;
  let drops = 0;
  let chains = 0;

  /** Acquire a free slot, or null when the pool is exhausted. */
  function acquire(): CoinSlot | null {
    for (let n = 0; n < slots.length; n++) {
      const s = slots[cursor];
      cursor = (cursor + 1) % slots.length;
      if (!s._alive) return s;
    }
    return null;
  }

  function release(s: CoinSlot): void {
    s._alive = false;
    s.active = false;
    s.sponsor = -1;
    s.chain = -1;
  }

  /**
   * Is `z` clear of every live vehicle in `lane`? The test measures to the
   * vehicle's BOX (half its model length), so `minVehicleGap` is clearance
   * beyond the bodywork rather than to a centre point.
   *
   * Kicked wrecks leave their lane entirely, so the same-lane test is by
   * LATERAL PROXIMITY rather than by the entity's original lane index: a wreck
   * cartwheeling across the shoulder is not an obstruction a chain has to
   * respect, and it stops being one the moment it is out of the lane.
   */
  function vehicleClear(
    laneX: number,
    z: number,
    vehicles: readonly VehicleEntity[],
  ): boolean {
    for (let i = 0; i < vehicles.length; i++) {
      const v = vehicles[i];
      if (!isLive(v)) continue;
      if (Math.abs(v.x - laneX) > TUNING.road.laneWidth * 0.5) continue;
      const half = modelLength(v.model) / 2;
      if (z > v.z - half - C.minVehicleGap && z < v.z + half + C.minVehicleGap) {
        return false;
      }
    }
    return true;
  }

  /**
   * Is the whole span [zMin, zMax] clear of coins from OTHER chains in `lane`?
   *
   * Checked ONCE for the chain, never per coin — the coins WITHIN a chain are
   * `spacing` (2.5 m) apart, which is far under `minCoinGap` by design, so a
   * per-coin test against the live pool would reject every coin after the first
   * and a "chain" would be a single coin. This is the anti-INTERLEAVE rule: two
   * chains must not merge into one unreadable dotted line, and it doubles as
   * the natural throttle on how often a lane can receive one.
   */
  function chainClear(laneX: number, zMin: number, zMax: number): boolean {
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      if (!s._alive) continue;
      if (Math.abs(s.x - laneX) > TUNING.road.laneWidth * 0.5) continue;
      if (s.z > zMin - C.minCoinGap && s.z < zMax + C.minCoinGap) return false;
    }
    return true;
  }

  function onVehicleSpawn(
    sponsor: VehicleEntity,
    closingSpeed: number,
    playerSpeed: number,
    runTime: number,
    vehicles: readonly VehicleEntity[],
  ): void {
    // (1) the chance draw — ALWAYS taken, so a rejected chain cannot shift the
    // stream for the chains that follow.
    const roll = rng();
    if (roll >= C.chainChance) return;
    // (2) and (3): the co-arrival delta + the count, taken before any geometry
    // is examined for the same reason.
    const delta = C.pairLeadMin + rng() * (C.pairLeadMax - C.pairLeadMin);
    const count =
      C.chainMin + Math.floor(rng() * (C.chainMax - C.chainMin + 1));

    if (playerSpeed <= 0 || closingSpeed <= 0) return;
    // THE CO-ARRIVAL SOLVE (header 2). The pairing is on the sponsor's FRONT —
    // the surface the player actually sees bearing down — not its centre, so a
    // 16 m semi and a 4.3 m hatchback give the same read.
    const frontArrival =
      -(sponsor.z + modelLength(sponsor.model) / 2) / closingSpeed;
    // The moment the chain's LAST coin reaches the player plane: `delta`
    // seconds before the sponsor's front does.
    const endArrival = frontArrival - delta;
    // ...and the moment its NEAREST coin does. The chain runs BACK toward the
    // player from the anchor, so the nearest coin leads the last one by the
    // chain's own length in player-travel time.
    const spanTime = ((count - 1) * C.spacing) / playerSpeed;
    const nearArrival = endArrival - spanTime;
    // The magnet reaches `magnetRadius` ahead, so a coin becomes COLLECTIBLE
    // that much sooner than it arrives.
    const nearCollect = nearArrival - C.magnetRadius / playerSpeed;
    // NEVER BORN ALREADY COLLECTIBLE, and never inside the START GRACE. Both
    // are the same test at different origins; the first is the invariant, the
    // second is the owner's opening-seconds rule.
    if (nearCollect <= 0) return;
    if (runTime + nearCollect < C.startGrace) return;
    // The FAR end of the chain (most negative z, met LAST — see the header).
    const endZ = -playerSpeed * endArrival;

    const laneX = laneCenterX(sponsor.lane);
    // ANTI-INTERLEAVE: the whole span is tested against other chains ONCE.
    if (!chainClear(laneX, endZ, endZ + (count - 1) * C.spacing)) return;

    // THE ANCHOR IS MANDATORY, and the chain must survive the vehicle clamp at
    // a readable length. Both are decided BEFORE a single slot is taken, so a
    // refused chain leaves no partial line on the road.
    if (!vehicleClear(laneX, endZ, vehicles)) return;
    let legal = 0;
    for (let i = 0; i < count; i++) {
      if (vehicleClear(laneX, endZ + i * C.spacing, vehicles)) legal++;
    }
    if (legal < C.minChainCoins) return;

    let laid = 0;
    for (let i = 0; i < count; i++) {
      // Back toward the player: i = 0 is the far end, i = count-1 the nearest
      // coin, which is therefore the FIRST one the player meets.
      const z = endZ + i * C.spacing;
      if (!vehicleClear(laneX, z, vehicles)) continue;
      const s = acquire();
      if (s === null) {
        // Pool exhausted: drop the REST of the chain and count it once.
        drops++;
        break;
      }
      s._alive = true;
      s.active = true;
      s.x = laneX;
      s.z = z;
      s.sponsor = sponsor.id;
      s.chain = chains;
      // A per-coin spin/bob phase so a chain is a shimmer, not a rank of
      // identical discs. Deterministic (a serial, not a draw) so it costs no
      // randomness and is safe to hash.
      s.phase = (serial % 64) / 64;
      serial++;
      laid++;
    }
    if (laid > 0) chains++;
  }

  function sponsorCoins(vehicleId: number): number {
    let n = 0;
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      if (s._alive && s.sponsor === vehicleId) n++;
    }
    return n;
  }

  function update(dt: number, playerSpeed: number): void {
    const dz = playerSpeed * dt;
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      if (!s._alive) continue;
      s.z += dz;
      if (s.z > C.despawnBehind) release(s);
    }
  }

  function collect(
    playerX: number,
    time: number,
    events: SimEvent[],
    scoring: Scoring,
  ): void {
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      if (!s._alive) continue;
      if (s.z < -C.magnetRadius) continue; // still out of reach ahead
      if (Math.abs(s.x - playerX) >= C.magnetHalfWidth) continue; // wrong lane
      events.push({
        type: "coin",
        coinId: s.id,
        x: s.x,
        z: s.z,
        value: TUNING.score.coin,
      });
      scoring.recordCoin(TUNING.score.coin, time);
      release(s);
    }
  }

  function reset(newSeed: number): void {
    for (let i = 0; i < slots.length; i++) release(slots[i]);
    cursor = 0;
    serial = 0;
    drops = 0;
    chains = 0;
    rng = mulberry32((newSeed ^ COIN_SEED_SALT) >>> 0);
  }

  return {
    coins: slots,
    get drops() {
      return drops;
    },
    get chains() {
      return chains;
    },
    onVehicleSpawn,
    sponsorCoins,
    update,
    collect,
    reset,
  };
}
